import { memo, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useControlResetSignal, useResettableControlState } from './controlResetContext';
import { useFieldControlId, useResolvedAriaLabel } from './fieldControlContext';
import { formatToStepPrecision } from '../../utils/stepPrecision';

function clampValue(value, min, max) {
  if (typeof min === 'number') value = Math.max(min, value);
  if (typeof max === 'number') value = Math.min(max, value);
  return value;
}

function roundToStep(value, step) {
  if (!step || step <= 0) return value;
  const rounded = Math.round(value / step) * step;
  return formatToStepPrecision(rounded, step);
}

function normalizeNumericValue(value, min, max, step, snapToStep) {
  const clamped = clampValue(getNumericValue(value), min, max);
  return snapToStep ? roundToStep(clamped, step) : clamped;
}

function formatDisplayValue(value, formatter, displayUnit) {
  if (formatter) return formatter(value);
  if (value === '' || value === null || value === undefined || Number.isNaN(value)) return '';
  return `${value}${displayUnit || ''}`;
}

function getNumericValue(value, fallback = 0) {
  if (value === '' || value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

const DraggableNumberInput = memo(({
  id,
  name,
  label,
  ariaLabel,
  value,
  onChange,
  onCommit,
  onDragCancel,
  min,
  max,
  step = 1,
  displayUnit = '',
  dragScale = 1,
  formatter,
  parser,
  disabled = false,
  readOnly = false,
  placeholder,
  inputMode = 'decimal',
  allowEmpty = false,
  emptyValue = '',
  wrapperClassName = '',
  inputWrapClassName = '',
  inputClassName = '',
  suffix = '',
  suffixClassName = '',
  inputSize = 'compact',
  stopPropagationOnKeyDown = false,
  liveUpdate = false,
  focusEditIntent = null,
  snapToStep = true,
}) => {
  const fallbackId = useId();
  const controlId = useFieldControlId(id, fallbackId);
  // This component's own label (when standalone) or a FormField caption names
  // the input through `for`. Keep an aria-label only where it says more.
  const resolvedAriaLabel = useResolvedAriaLabel(ariaLabel, id, label);

  const [draftValue, setDraftValue] = useResettableControlState('');
  const [manualEditing, setManualEditing] = useResettableControlState(false);
  const [dismissedFocusEditIntent, setDismissedFocusEditIntent] = useState(null);
  const dragRef = useRef(null);
  const inputRef = useRef(null);
  const latestPointerHandlersRef = useRef(null);
  const detachGesturePointerListenersRef = useRef(null);
  const handledFocusEditIntentRef = useRef(null);
  const pendingTypedDraftRef = useRef(null);
  const commitPendingDraftRef = useRef(null);
  const controlResetRevision = useControlResetSignal();
  const handledControlResetRevisionRef = useRef(controlResetRevision);
  const isAdjustable = !disabled && !readOnly;
  const intentEditing = isAdjustable
    && focusEditIntent != null
    && dismissedFocusEditIntent !== focusEditIntent;
  const isEditing = manualEditing || intentEditing;

  useLayoutEffect(() => {
    if (handledControlResetRevisionRef.current === controlResetRevision) return;
    handledControlResetRevisionRef.current = controlResetRevision;

    pendingTypedDraftRef.current = null;
    dragRef.current = null;
  }, [controlResetRevision]);

  useLayoutEffect(() => {
    if (
      !isAdjustable
      || focusEditIntent == null
      || handledFocusEditIntentRef.current === focusEditIntent
    ) {
      return;
    }
    handledFocusEditIntentRef.current = focusEditIntent;
    inputRef.current?.focus();
    inputRef.current?.select?.();
  }, [focusEditIntent, isAdjustable]);

  // The one place a value leaves this component as a commit.
  //
  // Any real commit (blur, arrow/page step, scrub release, or scrub cancel) supersedes an
  // uncommitted typed draft, so clear the pending-draft ref here. Otherwise the unmount cleanup
  // could re-commit a stale typed value over a newer keyboard/scrub commit (e.g. type 24,
  // ArrowUp -> 25, deselect -> stale 24 re-committed).
  //
  // `onChange` is republished with the committed value BEFORE `onCommit`, always, even when the
  // value has not moved since the last preview. Consumers that open a history gesture on
  // `onChange` and close it on `onCommit` (TimelineKeyframeRowControls) need the pair in that
  // order.
  const publishCommit = useCallback((committed) => {
    pendingTypedDraftRef.current = null;
    onChange?.(committed);
    onCommit?.(committed);
    return committed;
  }, [onChange, onCommit]);

  const commitValue = useCallback((nextValue, stepOverride = step) => (
    publishCommit(normalizeNumericValue(nextValue, min, max, stepOverride, snapToStep))
  ), [max, min, publishCommit, snapToStep, step]);

  const commitEmptyValue = useCallback(() => {
    if (!allowEmpty) {
      // Nothing is published, but the draft ref is still cleared: an empty box on a field that
      // does not allow empty must not leave a pending draft for the unmount cleanup to commit.
      pendingTypedDraftRef.current = null;
      return;
    }
    publishCommit(emptyValue);
  }, [allowEmpty, emptyValue, publishCommit]);

  const commitDraftValue = useCallback((nextDraftValue) => {
    const raw = nextDraftValue.trim();
    if (!raw) {
      commitEmptyValue();
      return false;
    }

    const parsed = parser ? parser(raw) : Number(raw.replace(displayUnit, '').trim());
    const numeric = Number(parsed);
    if (!Number.isFinite(numeric)) return false;
    commitValue(numeric);
    return true;
  }, [commitEmptyValue, commitValue, displayUnit, parser]);

  useLayoutEffect(() => {
    commitPendingDraftRef.current = commitDraftValue;
  }, [commitDraftValue]);

  useEffect(() => () => {
    const pendingDraftValue = pendingTypedDraftRef.current;
    pendingTypedDraftRef.current = null;
    if (pendingDraftValue === null) return;
    commitPendingDraftRef.current?.(pendingDraftValue);
  }, []);

  const handleInputChange = useCallback((event) => {
    if (!isAdjustable) return;
    const nextDraftValue = event.target.value;
    setDraftValue(nextDraftValue);
    if (focusEditIntent != null) setManualEditing(true);

    pendingTypedDraftRef.current = liveUpdate ? null : nextDraftValue;

    if (!liveUpdate) return;

    const raw = nextDraftValue.trim();
    if (!raw) {
      if (allowEmpty) {
        onChange?.(emptyValue);
        return;
      }
      onChange?.(normalizeNumericValue(0, min, max, step, snapToStep));
      return;
    }

    const parsed = parser ? parser(raw) : Number(raw.replace(displayUnit, '').trim());
    const numeric = Number(parsed);
    if (!Number.isFinite(numeric)) return;
    onChange?.(normalizeNumericValue(numeric, min, max, step, snapToStep));
  }, [
    allowEmpty,
    displayUnit,
    emptyValue,
    focusEditIntent,
    isAdjustable,
    liveUpdate,
    max,
    min,
    onChange,
    parser,
    setDraftValue,
    setManualEditing,
    snapToStep,
    step,
  ]);

  const handleInputBlur = useCallback(() => {
    if (!isAdjustable) return;
    pendingTypedDraftRef.current = null;
    setManualEditing(false);
    if (focusEditIntent != null) setDismissedFocusEditIntent(focusEditIntent);
    if (!commitDraftValue(draftValue)) {
      setDraftValue('');
    }
  }, [
    commitDraftValue,
    draftValue,
    focusEditIntent,
    isAdjustable,
    setDraftValue,
    setManualEditing,
  ]);

  const handleInputKeyDown = useCallback((event) => {
    if (stopPropagationOnKeyDown) {
      event.stopPropagation();
    }
    if (!isAdjustable) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setDraftValue('');
      setManualEditing(false);
      if (focusEditIntent != null) setDismissedFocusEditIntent(focusEditIntent);
      event.currentTarget.blur();
      return;
    }

    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'].includes(event.key)) {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' || event.key === 'PageDown' ? -1 : 1;
      const pageMultiplier = event.key === 'PageUp' || event.key === 'PageDown' ? 10 : 1;
      const effectiveStep = (event.shiftKey ? step / 10 : step) * pageMultiplier;
      const baseValue = getNumericValue(isEditing ? draftValue : value, getNumericValue(value));
      const nextValue = commitValue(baseValue + (direction * effectiveStep), effectiveStep);
      if (isEditing) {
        setDraftValue(String(nextValue));
      }
    }
  }, [
    commitValue,
    draftValue,
    focusEditIntent,
    isAdjustable,
    isEditing,
    setDraftValue,
    setManualEditing,
    step,
    stopPropagationOnKeyDown,
    value,
  ]);

  const handlePointerDown = useCallback((event) => {
    if (!isAdjustable) return;
    event.preventDefault();
    event.stopPropagation();
    setManualEditing(false);

    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startValue: getNumericValue(value),
      moved: false,
      // The last value published through onChange, so a cancelled gesture can commit exactly what
      // the consumer is already previewing. Null until the drag threshold is crossed.
      lastValue: null,
    };

    // ⛔ THE GESTURE OWNS THE RELEASE, NOT WHICHEVER ELEMENT IS UNDER THE CURSOR.
    // This used to rely on element-level pointerup plus `setPointerCapture`. A horizontal drag
    // on a field in a row (Scene3D camera position X, Y, Z) carries the cursor over its
    // neighbour, and when the grip did not hold, the release landed on the neighbour instead.
    // The pressed field never learned the gesture had ended, so it kept tracking the mouse with
    // the button UP, from the baseline it captured at the press — which, after a timeline scrub
    // discarded the draft, re-applied a stale pose over the user's edit. Caught by a live trace:
    // `down Camera position X` then `up Camera position Y hasDrag=false`.
    //
    // Window listeners make the end of the gesture independent of hit-testing and of capture.
    // They REPLACE the element-level move/up/cancel handlers rather than sitting beside them, so
    // there is exactly one path that ends a drag. The listeners call through
    // `latestPointerHandlersRef` so a mid-drag re-render never leaves them holding a stale
    // `onChange` closure.
    //
    // ⛔ CAPTURE PHASE, and that is load-bearing. A bubbling window listener fires AFTER every
    // ancestor's own pointerup, which REORDERS the release: the timeline keyframe row closes its
    // undo gesture on its wrapper's pointerup, then this field committed, the commit's onChange
    // saw no open gesture and opened a SECOND one, and the commit's onCommit closed that — two
    // undo steps for one drag (`TimelineKeyframeRowControls.test.jsx`, "writes on every move of
    // a scrub, inside ONE history gesture"). The capture phase runs before any ancestor, which
    // is the order the old element-level handler had: the field finishes its gesture first.
    detachGesturePointerListenersRef.current?.();
    const handleWindowPointerMove = (windowEvent) => latestPointerHandlersRef.current?.move(windowEvent);
    const handleWindowPointerUp = (windowEvent) => latestPointerHandlersRef.current?.up(windowEvent);
    const handleWindowPointerCancel = (windowEvent) => latestPointerHandlersRef.current?.cancel(windowEvent);
    window.addEventListener('pointermove', handleWindowPointerMove, true);
    window.addEventListener('pointerup', handleWindowPointerUp, true);
    window.addEventListener('pointercancel', handleWindowPointerCancel, true);
    detachGesturePointerListenersRef.current = () => {
      window.removeEventListener('pointermove', handleWindowPointerMove, true);
      window.removeEventListener('pointerup', handleWindowPointerUp, true);
      window.removeEventListener('pointercancel', handleWindowPointerCancel, true);
      detachGesturePointerListenersRef.current = null;
    };
  }, [isAdjustable, setManualEditing, value]);

  const handlePointerMove = useCallback((event) => {
    const dragState = dragRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    // ⛔ NOTHING IS PUBLISHED BELOW THE DRAG THRESHOLD, and that is what makes "unmoved" and
    // "nothing published" the same fact. This used to publish on EVERY move and only set `moved`
    // past 2px, so a 1-2px jitter on a plain click pushed a value that no release ever committed:
    // the release below the threshold enters text editing instead, and a cancel did nothing at
    // all. Consumers that open a history gesture on the first onChange were left holding it open,
    // and TimelineKeyframeRowControls carried a wrapper-level pointerup/pointercancel handler for
    // years to close it. With the publish gated, there is nothing to close.
    const deltaX = event.clientX - dragState.startX;
    if (!dragState.moved) {
      if (Math.abs(deltaX) <= 2) return;
      dragState.moved = true;
    }

    const effectiveStep = event.shiftKey ? step / 10 : step;
    const nextValue = dragState.startValue + (deltaX * effectiveStep * dragScale);
    dragState.lastValue = normalizeNumericValue(nextValue, min, max, effectiveStep, snapToStep);
    onChange?.(dragState.lastValue);
  }, [dragScale, max, min, onChange, snapToStep, step]);

  // The single owner of "this drag is over". Both pointerup and pointercancel end through here,
  // once: whichever arrives second finds no drag state and does nothing. The two handlers differ
  // only in WHICH value they commit, never in whether the gesture ends.
  const releaseDrag = useCallback((event) => {
    const dragState = dragRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return null;
    detachGesturePointerListenersRef.current?.();
    dragRef.current = null;
    return dragState;
  }, []);

  const handlePointerUp = useCallback((event) => {
    const dragState = releaseDrag(event);
    if (!dragState) return;

    if (dragState.moved) {
      const deltaX = event.clientX - dragState.startX;
      const effectiveStep = event.shiftKey ? step / 10 : step;
      const nextValue = dragState.startValue + (deltaX * effectiveStep * dragScale);
      commitValue(nextValue, effectiveStep);
      return;
    }

    setManualEditing(true);
    setDraftValue(String(value ?? ''));
    inputRef.current?.focus();
    inputRef.current?.select?.();
  }, [commitValue, dragScale, releaseDrag, setDraftValue, setManualEditing, step, value]);

  /*
   * ⛔ A CANCELLED DRAG ALWAYS ENDS. WHAT IT MEANS BELONGS TO WHOEVER OWNS THE GESTURE.
   *
   * A pointer gesture on this field either publishes nothing or publishes previews and then ends
   * exactly once. Below the drag threshold the press is a click: nothing is published, nothing is
   * reported, and a cancel there just releases the pointer. Past the threshold, `pointerup`
   * commits the value at the release point, and `pointercancel`:
   *
   *   - with `onDragCancel`: reports `{ startValue, lastValue }` and publishes NOTHING. The
   *     consumer owns a gesture session and has already decided what a cancel means there.
   *   - without it: commits the LAST PUBLISHED value exactly once.
   *
   * It used to do neither, so a touch or OS-cancelled scrub left the consumer's preview and any
   * viewport override live indefinitely, with no event that would ever clear them.
   *
   * ⛔ THE DEFAULT IS NOT A UNIVERSAL RULE, and picking one would have broken a ratified surface.
   * This repo already holds BOTH cancel meanings, each owned by a surface that decided it:
   * the sibling Slider track terminates a cancelled gesture with its last emitted value
   * (`useSliderControlGesture.js`, pinned by "terminates a pointer-cancelled gesture exactly once
   * with the last emitted value"), while `useTimelineClipTransformSession.cancelGesture` discards
   * the drag and RESTORES the pre-gesture pose. Committing unconditionally made this component a
   * second owner that disagreed with the clip transform panel -- and because these listeners are
   * on `window` in the capture phase it ran first, so a document write landed and the panel then
   * visually reverted it. Deferring keeps exactly one owner per surface.
   */
  const handlePointerCancel = useCallback((event) => {
    const dragState = releaseDrag(event);
    if (!dragState?.moved) return;
    if (onDragCancel) {
      onDragCancel({ startValue: dragState.startValue, lastValue: dragState.lastValue });
      return;
    }
    publishCommit(dragState.lastValue);
  }, [onDragCancel, publishCommit, releaseDrag]);

  useLayoutEffect(() => {
    latestPointerHandlersRef.current = {
      move: handlePointerMove,
      up: handlePointerUp,
      cancel: handlePointerCancel,
    };
  }, [handlePointerCancel, handlePointerMove, handlePointerUp]);

  // A field unmounted mid-drag must not leave window listeners behind.
  useEffect(() => () => detachGesturePointerListenersRef.current?.(), []);

  const fieldClassName = [
    'ui-text-input',
    inputSize === 'compact' ? 'ui-text-input--compact' : null,
    inputSize === 'dense' ? 'ui-text-input--compact ui-text-input--dense' : null,
    'draggable-number-input__field',
    suffix ? 'draggable-number-input__field--with-suffix' : null,
    inputClassName,
  ].filter(Boolean).join(' ');
  const ariaValueNow = getNumericValue(value, null);
  const inputValue = manualEditing
    ? draftValue
    : (intentEditing ? String(value ?? '') : formatDisplayValue(value, formatter, displayUnit));

  return (
    <div className={`draggable-number-input ${wrapperClassName}`.trim()}>
      {label ? (
        <label className="param-label" htmlFor={controlId}>
          {label}
        </label>
      ) : null}
      <div className={`draggable-number-input__input-wrap ${inputWrapClassName}`.trim()}>
        <input
          ref={inputRef}
          id={controlId}
          name={name}
          type="text"
          inputMode={inputMode}
          value={inputValue}
          className={fieldClassName}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          min={typeof min === 'number' ? min : undefined}
          max={typeof max === 'number' ? max : undefined}
          step={typeof step === 'number' ? step : undefined}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          onPointerDown={handlePointerDown}
          aria-label={resolvedAriaLabel}
          role="spinbutton"
          aria-valuemin={typeof min === 'number' ? min : undefined}
          aria-valuemax={typeof max === 'number' ? max : undefined}
          aria-valuenow={Number.isFinite(ariaValueNow) ? ariaValueNow : undefined}
        />
        {suffix && !isEditing ? (
          <span className={`draggable-number-input__suffix ${suffixClassName}`.trim()} aria-hidden="true">
            {suffix}
          </span>
        ) : null}
      </div>
    </div>
  );
});

DraggableNumberInput.displayName = 'DraggableNumberInput';

export default DraggableNumberInput;
